import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getVisualTuning } from './visualTuning.js';

/**
 * useSequence — drives a named reveal choreography from visualTuning.
 *
 * A sequence (e.g. LYNCH_REVEAL) is a map of BEAT NAME → ms offset. When
 * `enabled` flips from false → true we read the LIVE tuning snapshot and schedule
 * one setTimeout per beat; each fired beat is recorded so `has(beat)` flips true
 * and `onCue[beat]` runs once. When the last beat has fired, `done` is true.
 *
 * Keys ending in `Ms` (e.g. `tallyCountMs`, `moonRiseMs`) are NOT beats — they are
 * DURATION PARAMS consumed by cue handlers (how long a count-up runs, how long the
 * moon takes to rise). They are split out and returned via `params`, never timed.
 *
 * Timer discipline (mirrors hand/VoteScreen's effect):
 *   - The effect keys ONLY on `name` + `enabled`, so a mid-sequence parent
 *     re-render does NOT reset the timers.
 *   - All timers are cleared on disable and on unmount.
 *
 * Reduced-motion note: beat TIMES ARE KEPT here on purpose. The reveal dramaturgy
 * is synced room-wide to the narrator's voice, so every device must reach the same
 * beat at the same wall-clock moment regardless of motion preference. Reduced
 * motion is honored by the CONSUMER (it suppresses shake/particle-burst on those
 * beats), not by collapsing the schedule.
 *
 * FREEZE (Studio "Screens" gallery only): pass `freezeAt: [...beatNames]` to render
 * a sequenced reveal as a STILL. When present, the effect short-circuits: it reads
 * the tuning snapshot for `params`, marks EXACTLY the named beats as reached
 * synchronously (no setTimeout, so nothing counts up or animates on a wall clock),
 * sets `done` when all beats are frozen, and fires NO `onCue` callbacks — bursts,
 * shakes, and spotlight moves are transient/looping side effects that would flash or
 * repeat on a frozen frame, so a freeze is intentionally silent. The frozen visual is
 * whatever the screen's `has(beat)`-gated markup renders; the imperative FX are the
 * live path's concern. When `freezeAt` is absent (or undefined) the live scheduling
 * path below is byte-identical to before — freeze adds a branch, it changes nothing.
 */

const PARAM_SUFFIX = 'Ms';

/**
 * FreezeContext — the Studio "Screens" gallery wraps a sequenced state in a
 * provider carrying a per-sequence freeze map, so EVERY `useSequence` nested inside
 * (stage + hand screens alike) freezes to the requested beats with NO changes to
 * the screen files. The default is `null` ⇒ every live route (Host/Play/Stage/
 * Activity) runs the normal timed path. A `freezeAt` prop passed directly to
 * `useSequence` still wins over the context (explicit beats override).
 *
 * Shape: `{ [sequenceName]: readonly beatName[] }`. A sequence absent from the map
 * plays live even inside a provider (so a mixed screen can freeze one reveal and let
 * an unrelated ambient sequence run — not currently needed, but the contract is
 * unsurprising).
 */
export type FreezeMap = Record<string, readonly string[]>;
const FreezeContext = createContext<FreezeMap | null>(null);
export const FreezeProvider = FreezeContext.Provider;

export interface SequenceHandle {
  /** True once the named beat has fired. */
  has: (beat: string) => boolean;
  /** True once every timed beat in the sequence has fired. */
  done: boolean;
  /** Duration params (the `*Ms` keys), e.g. { tallyCountMs: 1600 }. */
  params: Record<string, number>;
}

export interface UseSequenceOptions {
  /** When true, (re)arm the sequence. False clears all pending timers. */
  enabled: boolean;
  /** Optional per-beat callback, fired once as each beat lands. */
  onCue?: Record<string, () => void>;
  /**
   * FREEZE mode (Studio "Screens" gallery only). When provided, the sequence is NOT
   * scheduled on a timeline — instead exactly these beats are marked reached at once
   * and no `onCue` fires. Absent/undefined ⇒ normal timed playback (live path). An
   * empty array is a valid freeze: no beats reached (the pre-reveal / beat-0 still).
   */
  freezeAt?: readonly string[];
}

export function useSequence(
  name: string,
  { enabled, onCue, freezeAt: freezeAtProp }: UseSequenceOptions,
): SequenceHandle {
  const [reached, setReached] = useState<Set<string>>(() => new Set());
  const [done, setDone] = useState(false);
  const [params, setParams] = useState<Record<string, number>>({});

  // Keep the latest onCue in a ref so changing the callback object between
  // renders does NOT re-run the scheduling effect (which would reset timers).
  const onCueRef = useRef(onCue);
  onCueRef.current = onCue;

  // Freeze source of truth: an explicit prop wins; else the gallery's FreezeContext
  // (keyed by this sequence's name); else null ⇒ live timed playback.
  const freezeMap = useContext(FreezeContext);
  const freezeAt = freezeAtProp ?? freezeMap?.[name];

  // Serialize freezeAt so the effect re-runs when the frozen beat SET changes
  // (e.g. the gallery steps a state through its beats) without depending on a
  // fresh array identity every render.
  const freezeKey = freezeAt ? freezeAt.join('|') : null;

  useEffect(() => {
    if (!enabled) {
      // Disabled — reset so a later re-arm starts clean. No timers to clear.
      setReached(new Set());
      setDone(false);
      return;
    }

    // Read the LIVE snapshot at arm time (studio edits before this beat apply).
    const seq = getVisualTuning().sequences[name] ?? {};

    // ── FREEZE: mark exactly the requested beats reached, fire no cues, no timers. ──
    if (freezeAt) {
      const nextParams: Record<string, number> = {};
      const allBeats: string[] = [];
      for (const [key, value] of Object.entries(seq)) {
        if (typeof value !== 'number') continue;
        if (key.endsWith(PARAM_SUFFIX)) nextParams[key] = value;
        else allBeats.push(key);
      }
      setParams(nextParams);
      // Only freeze beats that actually exist in the sequence (guards typos).
      const frozen = new Set(freezeAt.filter((b) => allBeats.includes(b)));
      setReached(frozen);
      // `done` once every timed beat is frozen (or the sequence has no beats).
      setDone(allBeats.length === 0 || allBeats.every((b) => frozen.has(b)));
      return;
    }

    // Split beats (timed) from duration params (`*Ms`, consumed by handlers).
    const beats: Array<[string, number]> = [];
    const nextParams: Record<string, number> = {};
    for (const [key, value] of Object.entries(seq)) {
      if (typeof value !== 'number') continue;
      if (key.endsWith(PARAM_SUFFIX)) {
        nextParams[key] = value;
      } else {
        beats.push([key, value]);
      }
    }

    setReached(new Set());
    setDone(false);
    setParams(nextParams);

    if (beats.length === 0) {
      setDone(true);
      return;
    }

    const maxDelay = Math.max(...beats.map(([, ms]) => ms));
    const timers: ReturnType<typeof setTimeout>[] = [];

    for (const [beat, ms] of beats) {
      timers.push(
        setTimeout(() => {
          setReached((prev) => {
            if (prev.has(beat)) return prev;
            const next = new Set(prev);
            next.add(beat);
            return next;
          });
          onCueRef.current?.[beat]?.();
          if (ms >= maxDelay) setDone(true);
        }, ms),
      );
    }

    return () => {
      for (const t of timers) clearTimeout(t);
    };
    // Intentionally NARROW deps: only re-arm when the sequence name, the enabled
    // transition, or the frozen beat set changes. onCue is read via ref; parent
    // re-renders and live-tuning edits mid-sequence must NOT reset the running
    // timers. `freezeKey` is the stable serialization of `freezeAt`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, enabled, freezeKey]);

  return {
    has: (beat: string) => reached.has(beat),
    done,
    params,
  };
}
